import apiSlice from 'renderer/shared/state/apiSlice';
import {
  DashboardGroupedDataResponse,
  DashboardGroupedDataResponseSchema,
  RecentUsersRequest,
  RecentUsersResponse,
  RecentUsersResponseSchema,
  RefundsRequest,
  RefundsResponse,
  RefundsResponseSchema,
  SalesInsightsRequest,
  SalesInsightsResponse,
  SalesInsightsResponseSchema,
  SalesSegmentTransactionsRequest,
  SalesSegmentTransactionsResponse,
  SalesSegmentTransactionsResponseSchema,
  UntaggedRenewalTransactionsRequest,
  UntaggedRenewalTransactionsResponse,
  UntaggedRenewalTransactionsResponseSchema,
} from '../schemas/dashboard.schemas';

const dashboardApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch Grouped Data (Purchases, Users by Product, Platform, Host App)
    getDashboardGroupedData: builder.query<DashboardGroupedDataResponse, void>({
      query: () => ({
        url: '/dashboard/grouped-data',
        method: 'GET',
      }),
      providesTags: ['Dashboard'],
      extraOptions: {
        responseSchema: DashboardGroupedDataResponseSchema,
      },
    }),

    // Fetch Recent Users (Paginated)
    getRecentUsers: builder.query<RecentUsersResponse, RecentUsersRequest>({
      query: ({ page = 1, limit = 10 }) => ({
        url: `/dashboard/recent-users?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Dashboard', 'User'],
      extraOptions: {
        responseSchema: RecentUsersResponseSchema,
      },
    }),

    // Fetch Refunds (Paginated)
    getRefunds: builder.query<RefundsResponse, RefundsRequest>({
      query: ({ page = 1, limit = 10 }) => ({
        url: `/dashboard/refunds?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Dashboard', 'Refund'],
      extraOptions: {
        responseSchema: RefundsResponseSchema,
      },
    }),
    getSalesInsights: builder.query<
      SalesInsightsResponse,
      SalesInsightsRequest
    >({
      query: (body) => ({
        url: '/dashboard/sales-insights',
        method: 'POST',
        body,
      }),
      extraOptions: {
        responseSchema: SalesInsightsResponseSchema,
      },
    }),
    getSalesSegmentTransactions: builder.query<
      SalesSegmentTransactionsResponse,
      SalesSegmentTransactionsRequest
    >({
      query: (body) => ({
        url: '/dashboard/sales-segment-transactions',
        method: 'POST',
        body,
      }),
      extraOptions: {
        responseSchema: SalesSegmentTransactionsResponseSchema,
      },
    }),
    getUntaggedRenewalTransactions: builder.query<
      UntaggedRenewalTransactionsResponse,
      UntaggedRenewalTransactionsRequest
    >({
      query: (body) => ({
        url: '/dashboard/untagged-renewal-transactions',
        method: 'POST',
        body,
      }),
      extraOptions: {
        responseSchema: UntaggedRenewalTransactionsResponseSchema,
      },
    }),
    exportSalesCsv: builder.mutation<
      Blob,
      {
        from: string;
        to: string;
        fileName: string;
        fields: string[];
        productIds?: string[];
        includeGrandTotal?: boolean;
        includeProductTotals?: boolean;
        includeHostAppTotals?: boolean;
        includePlatformTotals?: boolean;
        includeChurnSummary?: boolean;
        churnDenominatorFrom?: string;
        churnDenominatorTo?: string;
        churnRenewalsFrom?: string;
        churnRenewalsTo?: string;
      }
    >({
      query: (body) => ({
        url: '/dashboard/export-sales',
        method: 'POST',
        body,
        responseHandler: async (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetDashboardGroupedDataQuery,
  useGetRecentUsersQuery,
  useGetRefundsQuery,
  useGetSalesInsightsQuery,
  useLazyGetSalesSegmentTransactionsQuery,
  useLazyGetUntaggedRenewalTransactionsQuery,
  useExportSalesCsvMutation,
} = dashboardApi;

export default dashboardApi;
